import { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { toast } from 'sonner';
import { Sparkles, Smile, Hash, AtSign, Loader2 } from 'lucide-react';

const EMOJI_SETS = {
  frequenti: ['😍','💇','✨','💅','🔥','❤️','👏','💪','🎉','⭐'],
  bellezza: ['💇‍♀️','💇‍♂️','💅','💆‍♀️','💆‍♂️','🧖‍♀️','✂️','🪮','💈','🧴'],
  emozioni: ['😊','🥰','😎','💕','🙌','👌','💯','🤩','😘','🫶'],
  simboli: ['✨','⭐','🌟','💫','🔥','❤️','💜','💙','🤍','🖤'],
  natura: ['🌸','🌺','🌹','🌻','🍃','☀️','🌙','💐','🦋','🌈'],
};

const HASHTAG_SETS = {
  parrucchiere: ['#parrucchiere','#hairstyle','#capelli','#haircut','#salone','#hairdresser','#taglio','#piega','#colore','#balayage'],
  estetica: ['#estetica','#beauty','#bellezza','#skincare','#trattamento','#relax','#benessere','#spa','#cura','#glow'],
  social: ['#instagood','#photooftheday','#love','#follow','#style','#trend','#viral','#reels','#fashion','#selfcare'],
  business: ['#salonlife','#madeinitaly','#prenota','#nuovaapertura','#promozione','#offerta','#novita','#consiglio','#tutorial','#beforeandafter'],
};

const ContentToolbar = ({ value, onChange, platform = 'instagram' }) => {
  const { API, getAuthHeaders } = useAuth();
  const textareaRef = useRef(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [hashOpen, setHashOpen] = useState(false);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiTopic, setAiTopic] = useState('');
  const [aiGoal, setAiGoal] = useState('');

  const insertText = (text) => {
    const current = value || '';
    const needsSpace = current.length > 0 && !current.endsWith(' ') && !current.endsWith('\n');
    onChange(current + (needsSpace ? ' ' : '') + text);
  };

  const generateCaption = async () => {
    if (!aiTopic.trim()) {
      toast.error('Inserisci un argomento');
      return;
    }
    setAiLoading(true);
    try {
      const res = await axios.post(`${API}/ai/social-caption`, {
        topic: aiTopic,
        goal: aiGoal,
        platform,
        include_hashtags: true,
        include_emojis: true,
      }, getAuthHeaders());
      onChange(res.data.caption || '');
      setAiOpen(false);
      setAiTopic('');
      setAiGoal('');
      toast.success('Caption generata con AI!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nella generazione AI');
    } finally { setAiLoading(false); }
  };

  return (
    <TooltipProvider delayDuration={300}>
      <div className="flex items-center gap-0.5 pt-1.5 border-t border-border/40 mt-1">
        {/* AI Creator */}
        <Popover open={aiOpen} onOpenChange={setAiOpen}>
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 text-violet-500 hover:text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-950/30"
                  data-testid="ai-creator-btn"
                >
                  <Sparkles className="w-4 h-4" />
                </Button>
              </PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent side="bottom"><p>AI Creator</p></TooltipContent>
          </Tooltip>
          <PopoverContent className="w-80 p-4" align="start" side="bottom">
            <div className="space-y-3">
              <div className="flex items-center gap-2 pb-1">
                <Sparkles className="w-4 h-4 text-violet-500" />
                <span className="text-sm font-semibold">AI Caption Creator</span>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Argomento *</Label>
                <Input
                  value={aiTopic}
                  onChange={e => setAiTopic(e.target.value)}
                  placeholder="Es. Nuovo trattamento colore"
                  className="h-8 text-sm"
                  data-testid="ai-topic-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Obiettivo</Label>
                <Textarea
                  value={aiGoal}
                  onChange={e => setAiGoal(e.target.value)}
                  placeholder="Es. Promuovere il servizio e attirare nuovi clienti"
                  rows={2}
                  className="text-sm resize-none"
                  data-testid="ai-goal-input"
                />
              </div>
              <Button
                type="button"
                onClick={generateCaption}
                disabled={aiLoading || !aiTopic.trim()}
                className="w-full h-8 text-sm bg-violet-600 hover:bg-violet-700"
                data-testid="ai-generate-btn"
              >
                {aiLoading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
                {aiLoading ? 'Generazione...' : 'Genera Caption'}
              </Button>
            </div>
          </PopoverContent>
        </Popover>

        <div className="w-px h-4 bg-border/60 mx-0.5" />

        {/* Emoji Picker */}
        <Popover open={emojiOpen} onOpenChange={setEmojiOpen}>
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid="emoji-btn">
                  <Smile className="w-4 h-4" />
                </Button>
              </PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent side="bottom"><p>Emoji</p></TooltipContent>
          </Tooltip>
          <PopoverContent className="w-64 p-3" align="start" side="bottom">
            <div className="space-y-2.5 max-h-[280px] overflow-y-auto pr-1">
              {Object.entries(EMOJI_SETS).map(([category, emojis]) => (
                <div key={category}>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 font-medium">{category}</p>
                  <div className="flex flex-wrap gap-0.5">
                    {emojis.map(emoji => (
                      <button
                        key={emoji}
                        type="button"
                        onClick={() => { insertText(emoji); }}
                        className="w-8 h-8 flex items-center justify-center rounded hover:bg-muted transition-colors text-lg"
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </PopoverContent>
        </Popover>

        {/* Hashtag */}
        <Popover open={hashOpen} onOpenChange={setHashOpen}>
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid="hashtag-btn">
                  <Hash className="w-4 h-4" />
                </Button>
              </PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent side="bottom"><p>Hashtag</p></TooltipContent>
          </Tooltip>
          <PopoverContent className="w-72 p-3" align="start" side="bottom">
            <div className="space-y-2.5 max-h-[280px] overflow-y-auto pr-1">
              {Object.entries(HASHTAG_SETS).map(([category, tags]) => (
                <div key={category}>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 font-medium">{category}</p>
                  <div className="flex flex-wrap gap-1">
                    {tags.map(tag => (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => { insertText(tag); }}
                        className="px-2 py-0.5 text-xs rounded-full border hover:bg-primary/10 hover:border-primary/30 transition-colors"
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </PopoverContent>
        </Popover>

        {/* Mention */}
        <Popover open={mentionOpen} onOpenChange={setMentionOpen}>
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid="mention-btn">
                  <AtSign className="w-4 h-4" />
                </Button>
              </PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent side="bottom"><p>Menzione</p></TooltipContent>
          </Tooltip>
          <PopoverContent className="w-56 p-3" align="start" side="bottom">
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground">Inserisci menzione</p>
              <div className="flex gap-1.5">
                <Input
                  placeholder="@username"
                  className="h-8 text-sm"
                  data-testid="mention-input"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const val = e.target.value.trim();
                      if (val) {
                        insertText(val.startsWith('@') ? val : `@${val}`);
                        e.target.value = '';
                        setMentionOpen(false);
                      }
                    }
                  }}
                />
                <Button
                  type="button"
                  size="sm"
                  className="h-8 px-3"
                  onClick={() => {
                    const input = document.querySelector('[data-testid="mention-input"]');
                    if (input?.value?.trim()) {
                      const val = input.value.trim();
                      insertText(val.startsWith('@') ? val : `@${val}`);
                      input.value = '';
                      setMentionOpen(false);
                    }
                  }}
                >
                  Inserisci
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </TooltipProvider>
  );
};

export default ContentToolbar;
